/**
 * DEVLOGS-TEMP — the queue between `log()` and the network.
 *
 * The four invariants, in the house's own words (logger.ts:320-332):
 *
 *   NEVER BLOCKS THE WRITER  push() returns immediately; the flush is scheduled.
 *   BOUNDED                  past maxEntries OR maxBytes the oldest are dropped
 *                            and counted. A dead endpoint costs a fixed amount
 *                            of memory, not an OOM.
 *   SINGLE-FLIGHT            at most one POST in the air. A slow sink cannot
 *                            become a socket storm.
 *   NO RETRIES               a failed batch is discarded and counted.
 *
 * One behaviour beyond createLogDrain: DEGRADE. After N consecutive failures the
 * batcher raises its own floor to `warn` and emits one record saying so. The
 * rule is that an unhappy pipeline does LESS, never more — degrade drops harder,
 * it still does not resend.
 */

import { BoundedQueue, estimateSize } from "./ring.ts";
import type { DevLogTransport } from "./transport.ts";
import type { DevLogBatch, DevLogEnvelope, DevLogRecord, Level } from "./types.ts";

export type FlushReason =
  | "timer"
  | "size"
  | "bytes"
  | "level"
  | "unload"
  | "manual"
  | "reconnect";

export type EnvelopeSource = () => Omit<
  DevLogEnvelope,
  "sentAt" | "seqFrom" | "seqTo" | "drops"
>;

export interface BatcherOptions {
  transport: DevLogTransport;
  envelope: EnvelopeSource;
  /** Per-namespace drop counts to attach to the next envelope. */
  drops?: () => Record<string, number>;
  maxEntries?: number;
  maxBytes?: number;
  batchSize?: number;
  batchBytes?: number;
  flushMs?: number;
  degradeAfterFailures?: number;
  degradeCooldownMs?: number;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
  /** Called when the server says stop permanently. */
  onDisabled?: () => void;
}

export interface BatcherStats {
  queued: number;
  bytes: number;
  dropped: number;
  sent: number;
  failed: number;
  batches: number;
  inFlight: boolean;
  degraded: boolean;
  disabled: boolean;
  lastFailureAt: number | null;
}

export interface Batcher {
  push(record: DevLogRecord): void;
  flush(reason: FlushReason): void;
  flushNow(reason: FlushReason): Promise<void>;
  /** Minimum level currently admitted — raised while degraded. */
  floor(): Level | null;
  stats(): BatcherStats;
  close(): void;
}

const DEFAULTS = {
  maxEntries: 500,
  maxBytes: 1_000_000,
  batchSize: 100,
  batchBytes: 128_000,
  flushMs: 2000,
  degradeAfterFailures: 3,
  degradeCooldownMs: 30_000,
};

export function createBatcher(options: BatcherOptions): Batcher {
  const maxEntries = options.maxEntries ?? DEFAULTS.maxEntries;
  const maxBytes = options.maxBytes ?? DEFAULTS.maxBytes;
  const batchSize = options.batchSize ?? DEFAULTS.batchSize;
  const batchBytes = options.batchBytes ?? DEFAULTS.batchBytes;
  const flushMs = options.flushMs ?? DEFAULTS.flushMs;
  const degradeAfter = options.degradeAfterFailures ?? DEFAULTS.degradeAfterFailures;
  const degradeCooldownMs = options.degradeCooldownMs ?? DEFAULTS.degradeCooldownMs;
  const now = options.now ?? Date.now;
  const schedule =
    options.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancel =
    options.cancel ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));

  const queue = new BoundedQueue<DevLogRecord>({
    maxEntries,
    maxBytes,
    sizeOf: (record) => estimateSize(record),
  });

  let timer: unknown = null;
  let inFlight = false;
  let closed = false;
  let disabled = false;
  let consecutiveFailures = 0;
  let degradedUntil = 0;
  let sent = 0;
  let failed = 0;
  let batches = 0;
  let lastFailureAt: number | null = null;

  function armTimer(): void {
    if (timer !== null || closed || disabled) return;
    try {
      timer = schedule(() => {
        timer = null;
        void run("timer");
      }, flushMs);
      (timer as { unref?: () => void })?.unref?.();
    } catch {
      timer = null;
    }
  }

  function disarmTimer(): void {
    if (timer === null) return;
    try {
      cancel(timer);
    } catch {
      /* fire-and-forget */
    }
    timer = null;
  }

  function buildBatch(records: DevLogRecord[]): DevLogBatch | null {
    let base: ReturnType<EnvelopeSource>;
    try {
      base = options.envelope();
    } catch {
      return null;
    }
    let drops: Record<string, number> = {};
    try {
      drops = options.drops?.() ?? {};
    } catch {
      drops = {};
    }
    const dropped = queue.takeDropped();
    if (dropped > 0) drops["__queue"] = (drops["__queue"] ?? 0) + dropped;

    const seqs = records.map((record) => record.seq);
    const envelope: DevLogEnvelope = {
      ...base,
      sentAt: safe(() => now(), 0),
      seqFrom: seqs.length > 0 ? Math.min(...seqs) : 0,
      seqTo: seqs.length > 0 ? Math.max(...seqs) : 0,
    };
    if (Object.keys(drops).length > 0) envelope.drops = drops;
    return { v: 1, env: envelope, rec: records };
  }

  function takeChunk(): DevLogRecord[] {
    const all = queue.drain();
    if (all.length <= batchSize) {
      // Also respect the byte budget for a small-but-fat batch.
      let bytes = 0;
      for (let i = 0; i < all.length; i += 1) {
        bytes += estimateSize(all[i]);
        if (bytes > batchBytes && i > 0) {
          queue.restore(all.slice(i));
          return all.slice(0, i);
        }
      }
      return all;
    }
    queue.restore(all.slice(batchSize));
    return all.slice(0, batchSize);
  }

  async function run(_reason: FlushReason): Promise<void> {
    if (inFlight || closed || disabled) return;
    if (queue.size === 0) return;
    inFlight = true;
    try {
      const records = takeChunk();
      if (records.length === 0) return;
      const batch = buildBatch(records);
      if (batch === null) {
        failed += records.length;
        return;
      }
      batches += 1;
      let result;
      try {
        result = await options.transport.send(batch);
      } catch {
        result = { ok: false };
      }
      if (result.disabled === true) {
        disabled = true;
        disarmTimer();
        queue.clear();
        try {
          options.onDisabled?.();
        } catch {
          /* fire-and-forget */
        }
        return;
      }
      if (result.ok) {
        sent += records.length;
        consecutiveFailures = 0;
        degradedUntil = 0;
      } else {
        // NO RETRY. The batch is gone, and the loss is counted.
        failed += records.length;
        lastFailureAt = safe(() => now(), 0);
        consecutiveFailures += 1;
        if (consecutiveFailures >= degradeAfter) {
          degradedUntil = safe(() => now(), 0) + degradeCooldownMs;
        }
      }
    } finally {
      inFlight = false;
      if (queue.size > 0) armTimer();
    }
  }

  return {
    push(record: DevLogRecord): void {
      if (closed || disabled) return;
      queue.push(record);
      if (queue.size >= batchSize || queue.bytes >= batchBytes) {
        void run(queue.size >= batchSize ? "size" : "bytes");
        return;
      }
      // An error is the thing you most want to see even if the page dies 100ms
      // later, so it jumps the timer onto the next microtask.
      if (record.lvl >= 50) {
        void Promise.resolve().then(() => run("level"));
        return;
      }
      armTimer();
    },

    flush(reason: FlushReason): void {
      void run(reason);
    },

    async flushNow(reason: FlushReason): Promise<void> {
      await run(reason);
    },

    floor(): Level | null {
      if (disabled) return null;
      if (degradedUntil > safe(() => now(), 0)) return 40;
      return null;
    },

    stats(): BatcherStats {
      return {
        queued: queue.size,
        bytes: queue.bytes,
        dropped: queue.dropped,
        sent,
        failed,
        batches,
        inFlight,
        degraded: degradedUntil > safe(() => now(), 0),
        disabled,
        lastFailureAt,
      };
    },

    close(): void {
      closed = true;
      disarmTimer();
      queue.clear();
    },
  };
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    const value = fn();
    return value === undefined || value === null ? fallback : value;
  } catch {
    return fallback;
  }
}
